import { api } from './client';

export interface Warehouse {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  warehouse_type: string;
  address?: string | null;
  is_storage?: boolean;
  department_id?: string | null;
  default_direction_id?: string | null;
  external_source?: string | null;  // бейдж імпорту з 1С
}

export interface WarehouseCreate {
  organization_id: string;
  // Auto-code (2026-06-02): null → backend генерує "WH-N".
  code: string | null;
  name: string;
  warehouse_type?: 'WHOLESALE' | 'RETAIL' | 'NTT';
  address?: string | null;
  is_storage?: boolean;
  department_id?: string | null;
  default_direction_id?: string | null;
}

export interface VatRate {
  id: string;
  code: string;
  name: string;
  rate_percent: number;
  is_active: boolean;
}

// TD-B-P5.5.3 (2026-06-01): tax_purposes для пропорційного ПДВ.
export interface CashFlowItemRef {
  id: string;
  code: string;
  name: string;
  flow_type: 'OPERATING' | 'INVESTING' | 'FINANCING';
  is_system: boolean;
  usage_count?: number;  // ч.732: документів з цією статтею
}

export interface TaxPurpose {
  id: string;
  code: 'TAXABLE' | 'EXEMPT' | 'EXPORT' | 'NON_BUSINESS' | 'NOT_OBJECT';
  name_uk: string;
  description: string | null;
}

// alembic 0261: податкове призначення доходів/затрат (BUSINESS/NON_BUSINESS).
export interface IncomeCostTaxPurpose {
  id: string;
  code: string;
  name_uk: string;
  description: string | null;
}

// Порція 2c: НУ-призначення активів/затрат (FK способу відображення ЗП).
export interface TaxPurposeAssetsCosts {
  id: string;
  code: string;
  name_uk: string;
  description: string | null;
}

// TD-B-P5.5.2 (2026-06-01): tax_purpose_rules engine.
export interface TaxPurposeRule {
  id: string;
  organization_id: string;
  tax_purpose_id: string;
  contractor_id: string | null;
  nomenclature_id: string | null;
  nomenclature_group_id: string | null;
  priority: number;
  is_active: boolean;
  deletion_mark: boolean;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxPurposeRuleCreate {
  organization_id: string;
  tax_purpose_id: string;
  contractor_id?: string | null;
  nomenclature_id?: string | null;
  nomenclature_group_id?: string | null;
  priority?: number;
  is_active?: boolean;
  comment?: string | null;
}

export interface TaxPurposeRuleUpdate {
  tax_purpose_id?: string;
  priority?: number;
  is_active?: boolean;
  comment?: string | null;
}

export interface Contract {
  id: string;
  contractor_id: string;
  code: string;
  name: string;
  contract_type: string;
  currency_id: string;
  // TD-CONTRACT-FIELDS (2026-06-06): розширені поля договору.
  civil_kind_id?: string | null;
  number?: string | null;
  contract_date?: string | null;
  valid_until?: string | null;
  is_foreign_economic?: boolean;
  // 0372/0373 (модель договорів): дефолти документів + основний договір.
  is_main?: boolean;
  price_type_id?: string | null;
  vat_rate_id?: string | null;
  discount_percent?: string;
  // 0380 (2026-07-25): умови оплати договору → підставляються у рахунок.
  payment_terms_type?: 'PREPAY' | 'PARTIAL' | 'POSTPAY' | 'ON_DELIVERY' | null;
  prepayment_percent?: string | null;
  payment_term_days?: number | null;
  // Представлення договору при друку (картка договору).
  print_deal_info?: boolean;
  print_name?: string | null;
}

export interface ContractDebtInfo {
  debt_amount: string;
  max_overdue_days: number;
  debt_control_amount: boolean;
  debt_limit: string | null;
  debt_control_days: boolean;
  payment_term_days: number | null;
}

export interface BankAccount {
  id: string;
  owner_type: string;
  owner_id: string;
  iban: string;
  currency_id: string;
  account_name: string | null;
  is_main: boolean;
  default_direction_id?: string | null;
  external_source?: string | null;  // бейдж імпорту з 1С
  /** ч.1231: формати файла виписки для цього рахунку (за банком з IBAN); перший — за замовчуванням. */
  statement_formats?: StatementFormatInfo[];
  /** ч.1268: для рахунку є увімкнене API-підключення банку («Підключення до банку»). */
  has_api_connection?: boolean;
  /** ч.1279: скільки ще чинних карток того самого рахунку (той самий власник, IBAN і валюта). */
  same_iban_cards?: number;
  /** ч.1279: банківських документів на картці і за який період — лише для дублів. */
  bank_documents?: number | null;
  first_document_date?: string | null;
  last_document_date?: string | null;
}

export interface BankAccountMergeResult {
  merged: number;
  moved: Record<string, number>;
}

export interface StatementFormatInfo {
  code: string;
  label: string;
  /** розширення файлів для вибору файла, напр. `.csv` */
  accept: string;
  /** де взяти такий файл у банку */
  hint: string;
}

export interface BankAccountCreate {
  organization_id: string;  // на бекенді стає owner_id; owner_type='ORGANIZATION'
  iban: string;
  currency_id: string;
  account_name?: string | null;
  is_main?: boolean;
  default_direction_id?: string | null;
}

export interface CashRegister {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  currency_id: string;
  default_direction_id?: string | null;
  external_source?: string | null;  // бейдж імпорту з 1С
}

export interface CashRegisterCreate {
  organization_id: string;
  // Auto-code (2026-06-02): null → backend генерує "CASH-N".
  code: string | null;
  name: string;
  currency_id: string;
  default_direction_id?: string | null;
}

export interface Employee {
  id: string;
  code: string | null;
  name: string;
  position_name: string | null;
  inn: string | null;
  hire_date?: string | null;  // для авто-стажу у лікарняному/відпускних
  employment_type?: string | null;  // STAFF/CIVIL_LAW/NO_CONTRACT
}

export interface CostItem {
  id: string;
  code: string;
  name: string;
  /** ч.1364: лише зі `with_usage` — скільки рядків документів посилаються на статтю. */
  usage_count?: number | null;
}

// Вид номенклатури (tenant-scoped, юзер створює). 2026-06-06.
export interface NomenclatureKind {
  id: string;
  code: string | null;
  name: string;
  is_service: boolean;
  is_seed: boolean;       // дефолтний — не можна видалити/перейменувати
  deletion_mark: boolean;
}

export interface NomenclatureKindCreate {
  code?: string | null;
  name: string;
  is_service?: boolean;
}

// Вид договору по ЦК (tenant-scoped CRUD). TD-CONTRACT-FIELDS 2026-06-06.
export interface ContractCivilKind {
  id: string;
  code: string | null;
  name: string;
  is_seed: boolean;       // дефолтний — не можна видалити/перейменувати
  deletion_mark: boolean;
}

export interface ContractCivilKindCreate {
  code?: string | null;
  name: string;
}

// Група контрагента (tenant-scoped CRUD). TD-GROUPS B 2026-06-06.
export interface ContractorGroup {
  id: string;
  code: string | null;
  name: string;
  is_seed: boolean;
  deletion_mark: boolean;
}

export interface ContractorGroupCreate {
  code?: string | null;
  name: string;
}

// Типи цін (alembic 0260). kind: BASE — ціна вручну; DYNAMIC — обчислена
// = ціна базового типу × (1 + наценка%/100).
export interface PriceType {
  id: string;
  code: string | null;
  name: string;
  currency_id: string;
  price_includes_vat: boolean;
  is_purchase: boolean;
  /** ч.921: як рахувати закупівельну ціну (діє для is_purchase). */
  purchase_calc_method: 'LAST' | 'AVERAGE' | 'MAX';
  /** ч.928: валютне коригування закупівельної ціни (курс НБУ, накопичена зміна). */
  fx_adjust_enabled?: boolean;
  fx_currency_id?: string | null;
  fx_threshold_percent?: string | null;
  fx_direction?: 'UP' | 'DOWN' | 'BOTH';
  kind: 'BASE' | 'DYNAMIC';
  base_price_type_id: string | null;
  markup_percent: string | null;
}

export interface PriceTypeCreate {
  code?: string | null;
  name: string;
  currency_id: string;
  price_includes_vat?: boolean;
  is_purchase?: boolean;
  purchase_calc_method?: 'LAST' | 'AVERAGE' | 'MAX';
  fx_adjust_enabled?: boolean;
  fx_currency_id?: string | null;
  fx_threshold_percent?: number | string | null;
  fx_direction?: 'UP' | 'DOWN' | 'BOTH';
  kind: 'BASE' | 'DYNAMIC';
  base_price_type_id?: string | null;
  markup_percent?: number | string | null;
}

// Рядок резолвнутого списку цін номенклатури (по типу ціни). Backend
// NomenclaturePriceOut: BASE → price реальна; DYNAMIC → computed_price обчислена.
export interface NomenclaturePriceRow {
  nomenclature_price_id: string | null;
  price_type_id: string;
  price_type_name: string;
  kind: 'BASE' | 'DYNAMIC';
  is_dynamic: boolean;
  currency_id: string;
  markup_percent: string | null;
  price: string | null;
  computed_price: string | null;
  price_period: string | null;
}

export interface NomenclaturePriceCreate {
  nomenclature_id: string;
  price_type_id: string;
  price: number | string;
  price_period: string;
  characteristic_id?: string | null;
  uom_id?: string | null;
}

// Матриця правил рахунків обліку номенклатури (alembic 0263).
// Виміри (nomenclature/kind/warehouse) порожні = «для всіх».
// Хоча б один рахунок має бути заданий (backend валідує).
export interface AccountRule {
  id: string;
  organization_id: string;
  nomenclature_id: string | null;
  nomenclature_kind_id: string | null;
  warehouse_id: string | null;
  account_id: string | null;
  transfer_account_id: string | null;
  off_balance_account_id: string | null;
  off_balance_transfer_account_id: string | null;
  /** 0417: податкове призначення ПДВ правила (per-org, як 1С). */
  tax_purpose_id?: string | null;
  tax_purpose_code?: string | null;
  priority: number;       // 1-10
  is_active: boolean;
  deletion_mark: boolean;
  comment: string | null;
  external_source?: string | null;   // «I» — імпорт з 1С (нестандартне правило)
  // Назви вимірів — резолв на backend (не залежить від ліміту фронта).
  nomenclature_name?: string | null;
  nomenclature_kind_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductionNonstandardRow {
  id: string;                       // 'rule-<uuid>' | 'card-<uuid>'
  nomenclature_name: string;
  kind_name: string;
  source: 'RULE' | 'CARD';
  account_code: string | null;
  usable: boolean;
  external_source?: string | null;
}

export interface AccountRuleCreate {
  organization_id: string;
  nomenclature_id?: string | null;
  nomenclature_kind_id?: string | null;
  warehouse_id?: string | null;
  account_id?: string | null;
  transfer_account_id?: string | null;
  off_balance_account_id?: string | null;
  off_balance_transfer_account_id?: string | null;
  tax_purpose_id?: string | null;
  priority?: number;
  is_active?: boolean;
  comment?: string | null;
}

export interface AccountRuleUpdate {
  nomenclature_id?: string | null;
  nomenclature_kind_id?: string | null;
  warehouse_id?: string | null;
  account_id?: string | null;
  transfer_account_id?: string | null;
  off_balance_account_id?: string | null;
  off_balance_transfer_account_id?: string | null;
  tax_purpose_id?: string | null;
  priority?: number;
  is_active?: boolean;
  comment?: string | null;
}

// Який рахунок СПРАЦЮЄ у проводці (read-only показ на картці номенклатури).
// Derived з матриці+картки через прод-резолвер. drift-guard: refs.test.ts.
export interface AccountRulePreview {
  account_id: string | null;
  account_code: string | null;
  account_name: string | null;
  source: 'rule' | 'card' | 'hardcode-fallback' | 'not-used-in-postings';
  matched_rule_id: string | null;
  warehouse_dependent: boolean;
  /** 2026-08-12: рівень правила-переможця для бейджа UI. */
  rule_level?: 'nomenclature' | 'kind' | 'warehouse' | 'org' | null;
  rule_kind_name?: string | null;
}

// Матриця рахунків обліку ОПЛАТ працівника (per-org, alembic 0283, фіча картки #2).
// Колонка-рахунок матриці, до якої прив'язане субконто.
export type PayrollAccountColumn = 'expense_account_id' | 'payroll_account_id'
  | 'pdfo_account_id' | 'wz_account_id' | 'esv_account_id';

// Тип субконто способу (ручний ввід, SPEC §4). subconto_id=null = «<з документа>».
export type PayrollSubcontoType = 'ref.employees' | 'ref.cost_items'
  | 'ref.projects' | 'ref.construction_objects';

export interface PayrollSubconto {
  account_column: PayrollAccountColumn;
  slot: number;                       // 1..3
  subconto_type: PayrollSubcontoType;
  subconto_id: string | null;         // null = «<з документа>» (авто-підстановка)
}

export interface PayrollAccountRule {
  id: string;
  organization_id: string;
  employee_id: string | null;
  payment_kind: string | null;
  department_id: string | null;
  employment_type: string | null;
  expense_account_id: string | null;
  payroll_account_id: string | null;
  pdfo_account_id: string | null;
  wz_account_id: string | null;
  esv_account_id: string | null;
  priority: number;
  is_active: boolean;
  deletion_mark: boolean;
  comment: string | null;
  tax_purpose_assets_costs_id: string | null;  // НУ-призначення способу (1С)
  external_source: string | null;  // «I» — імпорт з 1С (нестандартний рахунок)
  subconto: PayrollSubconto[];
  created_at: string;
  updated_at: string;
}

export interface PayrollAccountRuleCreate {
  organization_id: string;
  employee_id?: string | null;
  payment_kind?: string | null;
  department_id?: string | null;
  employment_type?: string | null;
  expense_account_id?: string | null;
  payroll_account_id?: string | null;
  pdfo_account_id?: string | null;
  wz_account_id?: string | null;
  esv_account_id?: string | null;
  priority?: number;
  is_active?: boolean;
  comment?: string | null;
  tax_purpose_assets_costs_id?: string | null;
  subconto?: PayrollSubconto[];
}

export type PayrollAccountRuleUpdate = Partial<Omit<PayrollAccountRuleCreate, 'organization_id'>>;

export interface PayrollAccountPreviewItem {
  account_id: string | null;
  account_code: string | null;
  source: 'rule' | 'card' | 'civil_default' | 'header' | 'hardcode';
}

// Які 5 рахунків спрацюють для працівника (read-only показ на картці).
export interface PayrollAccountPreview {
  expense: PayrollAccountPreviewItem;
  payroll: PayrollAccountPreviewItem;
  pdfo: PayrollAccountPreviewItem;
  wz: PayrollAccountPreviewItem;
  esv: PayrollAccountPreviewItem;
}

// Batch: ефективні рахунки per-employee для закладки «Рахунки обліку» документа.
export interface PayrollAccountsPreviewBatchRow extends PayrollAccountPreview {
  employee_id: string;
}
export interface PayrollAccountsPreviewBatch {
  rows: PayrollAccountsPreviewBatchRow[];
}

export interface NomenclatureSeries {
  id: string;
  nomenclature_id: string;
  code: string | null;
  name: string;
  expiry_date?: string | null;
  manufacture_date?: string | null;
}

export interface NomenclatureCharacteristic {
  id: string;
  nomenclature_id: string;
  code: string | null;
  name: string;
}

export const refs = {
  warehouses: (organizationId?: string) =>
    api.get<Warehouse[]>('/refs/warehouses', { params: { organization_id: organizationId } }).then((r) => r.data),
  vatRates: () => api.get<VatRate[]>('/refs/vat-rates').then((r) => r.data),
  // ORG-EDITOR.1 (2026-06-04): currencies lookup для inline bank-account
  // створення у OrganizationEditor.
  currencies: () => api.get<{ id: string; code: string; name: string }[]>('/refs/currencies').then((r) => r.data),
  // ORG-EDITOR.2 (2026-06-04): NBU banks lookup для auto-fill bank name з МФО.
  // 58 активних банків seed з https://bank.gov.ua/NBU_BankInfo/get_data_branch?json
  banks: () => api.get<{ mfo: string; name: string; edrpou: string | null }[]>('/refs/banks').then((r) => r.data),
  // 2026-06-02 (0230): UOM classifier для Nomenclature.base_uom_id.
  // ч.1410: увесь ДК 011-96; часто вживані першими, вибір — пошуком.
  uoms: () => api.get<{ id: string; code: string; name: string; short_name: string; international_code: string; kspovo_code: string | null; is_common: boolean }[]>('/refs/uoms').then((r) => r.data),
  // TD-B-P5.5.3 (2026-06-01): tax_purposes для пропорційного ПДВ.
  taxPurposes: () => api.get<TaxPurpose[]>('/refs/tax-purposes').then((r) => r.data),
  // ч.727: статті ДДС (системні платформи + власні tenant'а).
  cashFlowItems: () => api.get<CashFlowItemRef[]>('/refs/cash-flow-items').then((r) => r.data),
  // ч.732: власні статті ДДС — CRUD + обʼєднання з іншою статтею.
  cashFlowItemsOwn: {
    create: (p: { code?: string | null; name: string; flow_type: CashFlowItemRef['flow_type'] }) =>
      api.post<CashFlowItemRef>('/refs/cash-flow-items', p).then((r) => r.data),
    update: (id: string, p: { code?: string; name?: string; flow_type?: CashFlowItemRef['flow_type'] }) =>
      api.patch<CashFlowItemRef>(`/refs/cash-flow-items/${id}`, p).then((r) => r.data),
    remove: (id: string) => api.delete(`/refs/cash-flow-items/${id}`).then(() => undefined),
    merge: (id: string, targetId: string) =>
      api.post<{ merged_into: string; moved: Record<string, number>; total: number }>(
        `/refs/cash-flow-items/${id}/merge`, { target_id: targetId }).then((r) => r.data),
  },
  incomeCostTaxPurposes: () =>
    api.get<IncomeCostTaxPurpose[]>('/refs/income-cost-tax-purposes').then((r) => r.data),
  // Порція 2c: НУ-призначення активів/затрат (dropdown способу відображення ЗП).
  /** 2026-08-11: останнє відоме призначення номенклатури (з рядків приходів). */
  lastTaxPurpose: (organizationId: string, nomenclatureIds: string[]) =>
    api.get<Record<string, string>>('/refs/last-tax-purpose', {
      params: { organization_id: organizationId, nomenclature_ids: nomenclatureIds.join(',') },
    }).then((r) => r.data),
  taxPurposesAssetsCosts: () =>
    api.get<TaxPurposeAssetsCosts[]>('/refs/tax-purposes-assets-costs').then((r) => r.data),
  // TD-B-P5.5.2 (2026-06-01): tax_purpose_rules engine.
  taxPurposeRules: {
    list: (organization_id: string) =>
      api.get<TaxPurposeRule[]>('/refs/tax-purpose-rules', { params: { organization_id } }).then((r) => r.data),
    create: (payload: TaxPurposeRuleCreate) =>
      api.post<TaxPurposeRule>('/refs/tax-purpose-rules', payload).then((r) => r.data),
    update: (id: string, payload: TaxPurposeRuleUpdate) =>
      api.patch<TaxPurposeRule>(`/refs/tax-purpose-rules/${id}`, payload).then((r) => r.data),
    delete: (id: string) =>
      api.delete(`/refs/tax-purpose-rules/${id}`).then((r) => r.data),
  },
  contracts: (params?: { organization_id?: string; contractor_id?: string }) =>
    api.get<Contract[]>('/refs/contracts', { params }).then((r) => r.data),
  contractDebtInfo: (contractId: string) =>
    api.get<ContractDebtInfo>(`/refs/contracts/${contractId}/debt-info`).then((r) => r.data),
  bankAccounts: (organizationId?: string) =>
    api.get<BankAccount[]>('/refs/bank-accounts', { params: { organization_id: organizationId } }).then((r) => r.data),
  // Банк-рахунки КОНТРАГЕНТА (owner_type=CONTRACTOR), не юрособи.
  contractorBankAccounts: (contractorId?: string) =>
    api.get<BankAccount[]>('/refs/bank-accounts', { params: { contractor_id: contractorId } }).then((r) => r.data),
  cashRegisters: (organizationId?: string) =>
    api.get<CashRegister[]>('/refs/cash-registers', { params: { organization_id: organizationId } }).then((r) => r.data),
  employees: (
    organizationId?: string,
    opts?: { employment_type?: string; active_only?: boolean },
  ) =>
    api.get<Employee[]>('/refs/employees', {
      params: { organization_id: organizationId, ...opts },
    }).then((r) => r.data),
  costItems: () =>
    api.get<CostItem[]>('/refs/cost-items').then((r) => r.data),
  // ч.1364: статті витрат — власний довідник акаунта (сторінка «Статті витрат»).
  costItemsOwn: {
    listWithUsage: () =>
      api.get<CostItem[]>('/refs/cost-items', { params: { with_usage: true } }).then((r) => r.data),
    create: (p: { name: string; code?: string }) =>
      api.post<CostItem>('/refs/cost-items', p).then((r) => r.data),
    update: (id: string, p: { name?: string; code?: string }) =>
      api.patch<CostItem>(`/refs/cost-items/${id}`, p).then((r) => r.data),
    remove: (id: string) => api.delete(`/refs/cost-items/${id}`).then(() => undefined),
  },
  // Види номенклатури (tenant-scoped CRUD). 2026-06-06.
  nomenclatureKinds: {
    list: () =>
      api.get<NomenclatureKind[]>('/refs/nomenclature-kinds').then((r) => r.data),
    create: (payload: NomenclatureKindCreate) =>
      api.post<NomenclatureKind>('/refs/nomenclature-kinds', payload).then((r) => r.data),
    update: (id: string, payload: Partial<NomenclatureKindCreate>) =>
      api.patch<NomenclatureKind>(`/refs/nomenclature-kinds/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/nomenclature-kinds/${id}`).then(() => undefined),
  },
  // Види договорів по ЦК (tenant-scoped CRUD). TD-CONTRACT-FIELDS 2026-06-06.
  contractCivilKinds: {
    list: () =>
      api.get<ContractCivilKind[]>('/refs/contract-civil-kinds').then((r) => r.data),
    create: (payload: ContractCivilKindCreate) =>
      api.post<ContractCivilKind>('/refs/contract-civil-kinds', payload).then((r) => r.data),
    update: (id: string, payload: Partial<ContractCivilKindCreate>) =>
      api.patch<ContractCivilKind>(`/refs/contract-civil-kinds/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/contract-civil-kinds/${id}`).then(() => undefined),
  },
  // Групи контрагентів (tenant-scoped CRUD). TD-GROUPS B 2026-06-06.
  contractorGroups: {
    list: () =>
      api.get<ContractorGroup[]>('/refs/contractor-groups').then((r) => r.data),
    create: (payload: ContractorGroupCreate) =>
      api.post<ContractorGroup>('/refs/contractor-groups', payload).then((r) => r.data),
    update: (id: string, payload: Partial<ContractorGroupCreate>) =>
      api.patch<ContractorGroup>(`/refs/contractor-groups/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/contractor-groups/${id}`).then(() => undefined),
  },
  // Типи цін (tenant-scoped CRUD). alembic 0260, 2026-06-08.
  priceTypes: {
    list: () =>
      api.get<PriceType[]>('/refs/price-types').then((r) => r.data),
    create: (payload: PriceTypeCreate) =>
      api.post<PriceType>('/refs/price-types', payload).then((r) => r.data),
    update: (id: string, payload: Partial<PriceTypeCreate>) =>
      api.patch<PriceType>(`/refs/price-types/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/price-types/${id}`).then(() => undefined),
  },
  // Правила рахунків обліку номенклатури (per-org матриця, alembic 0263).
  accountRules: {
    list: (organization_id: string, nomenclature_id?: string) =>
      api.get<AccountRule[]>('/refs/account-rules', {
        params: { organization_id, ...(nomenclature_id ? { nomenclature_id } : {}) },
      }).then((r) => r.data),
    // Який рахунок СПРАЦЮЄ для (org, nomenclature, склад) — read-only показ.
    preview: (params: {
      organization_id: string;
      nomenclature_id: string;
      warehouse_id?: string;
      column?: string;
    }) =>
      api.get<AccountRulePreview>('/refs/account-rules/preview', { params }).then((r) => r.data),
    create: (payload: AccountRuleCreate) =>
      api.post<AccountRule>('/refs/account-rules', payload).then((r) => r.data),
    update: (id: string, payload: AccountRuleUpdate) =>
      api.patch<AccountRule>(`/refs/account-rules/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/account-rules/${id}`).then(() => undefined),
    // ч.647: нестандартні рахунки ВИРОБНИЧОЇ номенклатури (пагіновано).
    productionNonstandard: (params: { organization_id: string; limit?: number; offset?: number }) =>
      api.get<{ items: ProductionNonstandardRow[]; total: number; limit: number; offset: number }>(
        '/refs/account-rules/production-nonstandard', { params },
      ).then((r) => r.data),
  },
  // Правила рахунків обліку ОПЛАТ працівника (per-org матриця, alembic 0283).
  payrollAccountRules: {
    list: (organization_id: string, employee_id?: string) =>
      api.get<PayrollAccountRule[]>('/refs/payroll-account-rules', {
        params: { organization_id, ...(employee_id ? { employee_id } : {}) },
      }).then((r) => r.data),
    // Які 5 рахунків спрацюють для працівника — read-only показ на картці.
    preview: (params: {
      organization_id: string;
      employee_id: string;
      payment_kind?: string;
    }) =>
      api.get<PayrollAccountPreview>('/refs/payroll-account-rules/preview', { params }).then((r) => r.data),
    // Batch для закладки «Рахунки обліку» документа (header-коди з шапки).
    previewBatch: (payload: {
      organization_id: string;
      employee_ids: string[];
      payment_kind?: string;
      header_codes?: Record<string, string> | null;
    }) =>
      api.post<PayrollAccountsPreviewBatch>('/refs/payroll-account-rules/preview-batch', payload).then((r) => r.data),
    create: (payload: PayrollAccountRuleCreate) =>
      api.post<PayrollAccountRule>('/refs/payroll-account-rules', payload).then((r) => r.data),
    update: (id: string, payload: PayrollAccountRuleUpdate) =>
      api.patch<PayrollAccountRule>(`/refs/payroll-account-rules/${id}`, payload).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/refs/payroll-account-rules/${id}`).then(() => undefined),
  },
  // Ціни номенклатури (резолвнутий список по типах; DYNAMIC computed-on-read).
  nomenclaturePrices: {
    // ч.922: з organization_id (документи) закупівельний тип і розрахункові від
    // нього віддають ціну, ОБЧИСЛЕНУ з надходжень організації (остання/середня/
    // максимальна — за налаштуванням типу); без нього — ціни прайсу (картка).
    listByNomenclature: (nomenclatureId: string, opts?: { organization_id?: string; on_date?: string }) =>
      api.get<NomenclaturePriceRow[]>('/refs/nomenclature-prices', {
        params: { nomenclature_id: nomenclatureId, ...(opts?.organization_id
          ? { organization_id: opts.organization_id, on_date: opts.on_date || undefined } : {}) },
      }).then((r) => r.data),
    upsert: (payload: NomenclaturePriceCreate) =>
      api.post<NomenclaturePriceRow>('/refs/nomenclature-prices', payload).then((r) => r.data),
    remove: (priceId: string) =>
      api.delete(`/refs/nomenclature-prices/${priceId}`).then(() => undefined),
  },
  nomenclatureSeries: (nomenclatureId?: string) =>
    api.get<NomenclatureSeries[]>('/refs/nomenclature-series', {
      params: { nomenclature_id: nomenclatureId },
    }).then((r) => r.data),
  createNomenclatureSeries: (payload: {
    nomenclature_id: string; name: string; code?: string | null;
    expiry_date?: string | null; manufacture_date?: string | null;
  }) =>
    api.post<NomenclatureSeries>('/refs/nomenclature-series', payload).then((r) => r.data),
  updateNomenclatureSeries: (id: string, payload: {
    name?: string; code?: string | null;
    expiry_date?: string | null; manufacture_date?: string | null;
  }) =>
    api.patch<NomenclatureSeries>(`/refs/nomenclature-series/${id}`, payload).then((r) => r.data),
  deleteNomenclatureSeries: (id: string) =>
    api.delete(`/refs/nomenclature-series/${id}`).then(() => undefined),

  // Характеристики (варіанти товару) — підфіча картки #4 (2026-06-08).
  nomenclatureCharacteristics: (nomenclatureId?: string) =>
    api.get<NomenclatureCharacteristic[]>('/refs/nomenclature-characteristics', {
      params: { nomenclature_id: nomenclatureId },
    }).then((r) => r.data),
  createNomenclatureCharacteristic: (payload: {
    nomenclature_id: string; name: string; code?: string | null;
  }) =>
    api.post<NomenclatureCharacteristic>('/refs/nomenclature-characteristics', payload).then((r) => r.data),
  updateNomenclatureCharacteristic: (id: string, payload: { name?: string; code?: string | null }) =>
    api.patch<NomenclatureCharacteristic>(`/refs/nomenclature-characteristics/${id}`, payload).then((r) => r.data),
  deleteNomenclatureCharacteristic: (id: string) =>
    api.delete(`/refs/nomenclature-characteristics/${id}`).then(() => undefined),

  // default_direction_id (autopodstavka напрямку у документ).
  setWarehouseDirection: (warehouseId: string, directionId: string | null) =>
    api.patch<Warehouse>(`/refs/warehouses/${warehouseId}/direction`, {
      default_direction_id: directionId,
    }).then((r) => r.data),
  setCashRegisterDirection: (cashRegisterId: string, directionId: string | null) =>
    api.patch<CashRegister>(`/refs/cash-registers/${cashRegisterId}/direction`, {
      default_direction_id: directionId,
    }).then((r) => r.data),
  setBankAccountDirection: (bankAccountId: string, directionId: string | null) =>
    api.patch<BankAccount>(`/refs/bank-accounts/${bankAccountId}/direction`, {
      default_direction_id: directionId,
    }).then((r) => r.data),

  // Повний CRUD для admin-сторінок.
  createWarehouse: (p: WarehouseCreate) =>
    api.post<Warehouse>('/refs/warehouses', p).then((r) => r.data),
  updateWarehouse: (id: string, p: Partial<Omit<WarehouseCreate, 'organization_id'>>) =>
    api.patch<Warehouse>(`/refs/warehouses/${id}`, p).then((r) => r.data),
  removeWarehouse: (id: string) =>
    api.delete(`/refs/warehouses/${id}`).then(() => undefined),

  createCashRegister: (p: CashRegisterCreate) =>
    api.post<CashRegister>('/refs/cash-registers', p).then((r) => r.data),
  updateCashRegister: (id: string, p: Partial<Omit<CashRegisterCreate, 'organization_id'>>) =>
    api.patch<CashRegister>(`/refs/cash-registers/${id}`, p).then((r) => r.data),
  removeCashRegister: (id: string) =>
    api.delete(`/refs/cash-registers/${id}`).then(() => undefined),

  createBankAccount: (p: BankAccountCreate) =>
    api.post<BankAccount>('/refs/bank-accounts', p).then((r) => r.data),
  updateBankAccount: (id: string, p: Partial<Omit<BankAccountCreate, 'organization_id'>>) =>
    api.patch<BankAccount>(`/refs/bank-accounts/${id}`, p).then((r) => r.data),
  removeBankAccount: (id: string) =>
    api.delete(`/refs/bank-accounts/${id}`).then(() => undefined),
  /** ч.1279: обʼєднати картки того самого рахунку в основну (лише власник / адміністратор). */
  mergeBankAccounts: (mainId: string, duplicateIds: string[]) =>
    api.post<BankAccountMergeResult>('/refs/bank-accounts/merge', {
      main_id: mainId, duplicate_ids: duplicateIds,
    }).then((r) => r.data),
};
